import type { ContactFilterQuery } from "@/features/inbox/types/filter";

import {
  applyQuickFilter,
  parseQuickFilterParams,
  readQuickFilter,
  writeQuickFilterParams,
} from "@/features/common/helpers/quick-filter";
import { INBOX_ATTENTION_FIELDS } from "@/features/inbox/constants/quick-filters";
import {
  EMPTY_FILTER,
  getFieldArrayValue,
  isBooleanFieldActive,
  setExclusiveBooleanRule,
  upsertFieldArrayRule,
} from "@/features/inbox/helpers/filter-rows";
import {
  defaultFilters,
  type InboxFilter,
} from "@/features/inbox/stores/inbox";
import {
  SEARCH_FOR,
  SORT_OPTION,
  type InboxSortKey,
  type SearchFor,
} from "@/features/inbox/types/inbox";
import {
  CONVERSATION_STATUS,
  CONVERSATION_VIEW,
  type ConversationStatus,
  type ConversationView,
} from "@/types/conversation";

// Inbox list filters are URL-driven: the URL is the source of truth and the
// store is a cache seeded from it (so a link reproduces the exact list in a new
// tab / on reload). This module is the single owner of that URL ↔ filter
// mapping - `parseInboxFilters` (URL → store patch) and `buildInboxSearch`
// (store → query string for sidebar links) are exact inverses.

export const INBOX_PARAM = {
  VIEW: "view",
  ASSIGNEE: "assignee",
  // A custom inbox is a saved filter, so only its id travels in the URL - the
  // rules themselves are resolved from the cached inbox list. `view` can't carry
  // it: that param is typed `ConversationView` and lands on an `@IsEnum` DTO, so
  // a uuid in it is a 400.
  CUSTOM_VIEW: "customView",
  STATUS: "status",
  SORT: "sort",
  TEAM: "team",
  ATTENTION: "attention",
  SEARCH: "search",
  SEARCH_FOR: "searchFor",
  // The open contact tab (Activities, Billing, ...). Lives in the URL so it
  // survives switching conversations; Messages is the default and stays out.
  TAB: "tab",
} as const;

export const ASSIGNEE_TOKEN = {
  MINE: "mine",
  TEAMMATES: "teammates",
  UNASSIGNED: "unassigned",
  ALL: "all",
} as const;

export type AssigneeToken =
  (typeof ASSIGNEE_TOKEN)[keyof typeof ASSIGNEE_TOKEN];

/** Sentinel on the assignee axis, alongside `null` (unassigned) and "all".
 *  Mine / teammates / unassigned are mutually exclusive and partition "all",
 *  so they share one field rather than adding a parallel flag. */
export const ASSIGNED_TO_TEAMMATES = "teammates";

const VIEW_VALUES = new Set<string>(Object.values(CONVERSATION_VIEW));
const STATUS_VALUES = new Set<string>([
  ...Object.values(CONVERSATION_STATUS),
  "all",
]);
const SORT_VALUES = new Set<string>(Object.values(SORT_OPTION));

// The Follow-ups view sorts by due date unless the user picks another order.
export const impliedSort = (view: ConversationView): InboxSortKey =>
  view === CONVERSATION_VIEW.FOLLOW_UP
    ? SORT_OPTION.DUE_SOONEST
    : SORT_OPTION.NEWEST;

// Sort to apply when switching to `view`: Follow-ups jumps to due-soonest;
// leaving Follow-ups drops due-soonest back to newest; any other choice sticks.
export const coupledSort = (
  view: ConversationView,
  currentSort: InboxSortKey,
): InboxSortKey =>
  view === CONVERSATION_VIEW.FOLLOW_UP
    ? SORT_OPTION.DUE_SOONEST
    : currentSort === SORT_OPTION.DUE_SOONEST
      ? SORT_OPTION.NEWEST
      : currentSort;

// `assignedTo` (store) → token. Mirrors the assignment-tab derivation so the URL
// and the Mine/Unassigned/All tabs always agree. A specific id that isn't the
// current user collapses to "all" (the UI never assigns to another user).
export const assigneeToken = (
  assignedTo: InboxFilter["assignedTo"],
  currentUserId: string | null | undefined,
): string => {
  // Undefined is the unseeded default, which resolves to the viewer.
  if (assignedTo === undefined) return ASSIGNEE_TOKEN.MINE;
  if (assignedTo === null) return ASSIGNEE_TOKEN.UNASSIGNED;
  if (assignedTo === ASSIGNED_TO_TEAMMATES) return ASSIGNEE_TOKEN.TEAMMATES;
  if (currentUserId && assignedTo === currentUserId) return ASSIGNEE_TOKEN.MINE;

  return ASSIGNEE_TOKEN.ALL;
};

/**
 * Inverse of `assigneeToken`: the `assignedTo` a sidebar row's token points at.
 * Same mapping `parseInboxFilters` applies to the `assignee` param, so a row's
 * href and the state it parses back into can't drift.
 */
export const assignedToFromToken = (
  token: AssigneeToken,
  currentUserId: string | null | undefined,
): InboxFilter["assignedTo"] =>
  token === ASSIGNEE_TOKEN.UNASSIGNED
    ? null
    : token === ASSIGNEE_TOKEN.TEAMMATES
      ? ASSIGNED_TO_TEAMMATES
      : token === ASSIGNEE_TOKEN.ALL
        ? "all"
        : (currentUserId ?? "all");

const splitCsv = (value: string | null): string[] =>
  value ? value.split(",").filter(Boolean) : [];

const setOrDelete = (
  params: URLSearchParams,
  key: string,
  value: string,
): void => {
  if (value) params.set(key, value);
  else params.delete(key);
};

// Team, Echo-attention and the Filters drawer's facets are the filters the URL
// owns. The `adv` JSON blob went with the ad-hoc filter popover: an arbitrary
// rule set is now a custom inbox, whose rules live in the database behind
// `inbox=<id>` rather than in the query string. Attention and the drawer facets
// get named params instead of riding `adv`, because each is one known axis - the
// field name reads better than a serialized rule, and it round-trips.
export const parseInboxFilterQuery = (
  params: URLSearchParams,
): ContactFilterQuery => {
  const team = splitCsv(params.get(INBOX_PARAM.TEAM));

  let query = team.length
    ? upsertFieldArrayRule(EMPTY_FILTER, "team", team)
    : EMPTY_FILTER;

  const attention = params.get(INBOX_PARAM.ATTENTION) as
    | (typeof INBOX_ATTENTION_FIELDS)[number]
    | null;

  if (attention && INBOX_ATTENTION_FIELDS.includes(attention)) {
    query = setExclusiveBooleanRule(query, INBOX_ATTENTION_FIELDS, attention);
  }

  return applyQuickFilter(query, parseQuickFilterParams(params));
};

export const writeInboxFilterQuery = (
  params: URLSearchParams,
  filter: ContactFilterQuery,
): void => {
  setOrDelete(
    params,
    INBOX_PARAM.TEAM,
    getFieldArrayValue(filter, "team").join(","),
  );
  setOrDelete(
    params,
    INBOX_PARAM.ATTENTION,
    INBOX_ATTENTION_FIELDS.find((field) =>
      isBooleanFieldActive(filter, field),
    ) ?? "",
  );
  writeQuickFilterParams(params, readQuickFilter(filter));
};

// URL → store patch. `currentUserId` resolves the portable `mine` assignee token
// to the viewer, so a shared link shows the opener's own work.
export const parseInboxFilters = (
  params: URLSearchParams,
  currentUserId: string | null | undefined,
): Partial<InboxFilter> => {
  const rawView = params.get(INBOX_PARAM.VIEW);
  const view = (
    rawView && VIEW_VALUES.has(rawView) ? rawView : CONVERSATION_VIEW.ALL
  ) as ConversationView;

  const isMentions = view === CONVERSATION_VIEW.MENTIONS;

  const assignee = params.get(INBOX_PARAM.ASSIGNEE);
  // The drawer names people on the same axis the scope rows do, so when it has a
  // selection it wins outright. ANDing the two would let "Unassigned" and
  // "assigned to Ana" both apply, which no conversation can satisfy - an empty
  // list with every control claiming to be working.
  const pickedAssignees = parseQuickFilterParams(params).assignees;
  const assignedTo: InboxFilter["assignedTo"] =
    pickedAssignees.length > 0
      ? "all"
      : assignee === ASSIGNEE_TOKEN.UNASSIGNED
        ? null
        : assignee === ASSIGNEE_TOKEN.TEAMMATES
          ? ASSIGNED_TO_TEAMMATES
          : assignee === ASSIGNEE_TOKEN.ALL
            ? "all"
            : // A mention names you directly, so it has to surface whoever holds
              // the conversation - otherwise being tagged on a teammate's thread,
              // which is most of them, shows nothing.
              isMentions
              ? "all"
              : (currentUserId ?? "all");

  const rawStatus = params.get(INBOX_PARAM.STATUS);
  const status = (
    rawStatus && STATUS_VALUES.has(rawStatus)
      ? rawStatus
      : // Closing a conversation does not answer the question you were tagged in.
        isMentions
        ? "all"
        : CONVERSATION_STATUS.OPEN
  ) as ConversationStatus | "all";

  const rawSort = params.get(INBOX_PARAM.SORT);
  const sort = (
    rawSort && SORT_VALUES.has(rawSort) ? rawSort : impliedSort(view)
  ) as InboxSortKey;

  const rawSearchFor = params.get(INBOX_PARAM.SEARCH_FOR);
  const $for: SearchFor =
    rawSearchFor === SEARCH_FOR.MESSAGE
      ? SEARCH_FOR.MESSAGE
      : SEARCH_FOR.CONTACT;

  return {
    view,
    assignedTo,
    status,
    sort,
    customViewId: params.get(INBOX_PARAM.CUSTOM_VIEW),
    query: parseInboxFilterQuery(params),
    search: { $for, term: params.get(INBOX_PARAM.SEARCH) ?? "" },
  };
};

// store → query string (no leading `?`). Defaults are never written, so a clean
// list state yields a clean `/inbox/:ws` URL.
export const buildInboxSearch = (
  filter: InboxFilter,
  currentUserId: string | null | undefined,
): string => {
  const params = new URLSearchParams();

  if (filter.view !== CONVERSATION_VIEW.ALL) {
    params.set(INBOX_PARAM.VIEW, filter.view);
  }

  if (filter.customViewId)
    params.set(INBOX_PARAM.CUSTOM_VIEW, filter.customViewId);

  const token = assigneeToken(filter.assignedTo, currentUserId);

  if (token !== ASSIGNEE_TOKEN.MINE) params.set(INBOX_PARAM.ASSIGNEE, token);

  if (filter.status !== defaultFilters.status) {
    params.set(INBOX_PARAM.STATUS, String(filter.status));
  }

  if (filter.sort !== impliedSort(filter.view)) {
    params.set(INBOX_PARAM.SORT, filter.sort);
  }

  if (filter.search?.term) params.set(INBOX_PARAM.SEARCH, filter.search.term);
  if (filter.search?.$for && filter.search.$for !== SEARCH_FOR.CONTACT) {
    params.set(INBOX_PARAM.SEARCH_FOR, filter.search.$for);
  }

  writeInboxFilterQuery(params, filter.query ?? EMPTY_FILTER);

  return params.toString();
};

// Convenience for sidebar links: `/inbox/:ws?…` with defaults stripped.
export const buildInboxHref = (
  basePath: string,
  filter: InboxFilter,
  currentUserId: string | null | undefined,
): string => {
  const search = buildInboxSearch(filter, currentUserId);

  return search ? `${basePath}?${search}` : basePath;
};

/** A queue on every owner: a view, or the needs-you attention rule. */
export const workspaceInboxHref = (
  workspaceId: string,
  queue: ConversationView | "needsYou",
  currentUserId: string | null | undefined,
): string => {
  const filter: Partial<InboxFilter> =
    queue === "needsYou"
      ? {
          sort: SORT_OPTION.PRIORITY,
          query: setExclusiveBooleanRule(EMPTY_FILTER, [queue], queue),
        }
      : { view: queue, sort: coupledSort(queue, defaultFilters.sort) };

  return buildInboxHref(
    `/inbox/${workspaceId}`,
    { ...defaultFilters, assignedTo: "all", ...filter },
    currentUserId,
  );
};
